import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { parse, sortUrl, toSortOrder } from '../../utils/UrlUtil';
import Paginator from '../../components/Paginator';
import ExecutiveReportListTableHeader from './ExecutiveReportListTableHeader';
import { AccountContext } from '../../components/AccountContext';

const messages = defineMessages({
  teamName: { id: 'exec_report.mng_team_name', defaultMessage: 'Management Team Name' },
  managersCount: { id: 'exec_report.number_selected_survey', defaultMessage: 'Number of managers selected' },
  surveyTemplate: { id: 'exec_report.survey_template', defaultMessage: 'Survey Template' },
  created: { id: 'exec_report.created_on', defaultMessage: 'Created on' },
  updated: { id: 'exec_report.updated_on', defaultMessage: 'Updated on' },
});

const ExecutiveReportList = ({ connection: { edges, totalCount, indexFrom, indexTo, pageInfo }, loading }) => {
  const history = useHistory();
  const location = useLocation();
  const currentAccount = useContext(AccountContext);

  const { formatMessage, formatDate } = useIntl();

  const connectionRows = edges.map(({ node }) => ({
    ...node,
  }));

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  const teamNameHeader = formatMessage(messages.teamName);
  const numOfSurveyHeader = formatMessage(messages.managersCount);
  const createdHeader = formatMessage(messages.created);
  const updatedHeader = formatMessage(messages.updated);
  const surveyTemplateHeader = formatMessage(messages.surveyTemplate);

  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  const nameTemplate = (rowData) => (
    <Link to={`/accounts/${currentAccount}/executive-reports/${rowData.id}`} className="app-link">
      {rowData.teamName}
    </Link>
  );

  const createdTemplate = (rowData) => {
    const created = formatDate(moment(rowData.createdOn), {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
    return created;
  };

  const updatedTemplate = (rowData) => {
    const updated = formatDate(moment(rowData.updatedOn), {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
    return updated;
  };

  const titleTemplate = (rowData) => <p>{rowData.template.title}</p>;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <Panel>
              <div className="p-col-12">
                <ExecutiveReportListTableHeader />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={connectionRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={(event) => sortUrl(event, location, history)}
                  lazy
                  loading={loading}
                >
                  <Column
                    field="teamName"
                    header={teamNameHeader}
                    body={nameTemplate}
                    sortable
                    style={{ textAlign: 'left' }}
                  />
                  <Column field="numberOfSurveys" header={numOfSurveyHeader} />
                  <Column field="createdOn" header={createdHeader} body={createdTemplate} sortable />
                  <Column field="updatedOn" header={updatedHeader} body={updatedTemplate} sortable />
                  <Column field="title" header={surveyTemplateHeader} body={titleTemplate} sortable />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

ExecutiveReportList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ExecutiveReportList;
